export const BACK_PARAM = "from";

const INTERNAL_URL_ORIGIN = "https://hiive.local";

function hasUnsafeHrefCharacter(value: string) {
  for (let index = 0; index < value.length; index += 1) {
    const code = value.charCodeAt(index);

    if (value[index] === "\\" || code <= 31 || code === 127) {
      return true;
    }
  }

  return false;
}

export function isSafeInternalHref(
  href: string | null | undefined,
): href is string {
  const value = href?.trim();
  if (!value) return false;
  if (!value.startsWith("/") || value.startsWith("//")) return false;
  if (hasUnsafeHrefCharacter(value)) return false;

  try {
    const url = new URL(value, INTERNAL_URL_ORIGIN);
    return url.origin === INTERNAL_URL_ORIGIN && url.pathname.startsWith("/");
  } catch {
    return false;
  }
}

function normalizeInternalHref(href: string | null | undefined) {
  if (!isSafeInternalHref(href)) return null;

  const url = new URL(href, INTERNAL_URL_ORIGIN);
  return `${url.pathname}${url.search}${url.hash}`;
}

export function hrefFromPathnameAndSearch(
  pathname: string | null | undefined,
  search:
    | string
    | URLSearchParams
    | { toString: () => string }
    | null
    | undefined,
) {
  const path = normalizeInternalHref(pathname) ?? "/";
  const query = search?.toString() ?? "";
  return query ? `${path}?${query}` : path;
}

export function withFromParam(href: string, from: string): string {
  const safeHref = normalizeInternalHref(href);
  const safeFrom = normalizeInternalHref(from);
  if (!safeHref || !safeFrom) return href;

  const url = new URL(safeHref, INTERNAL_URL_ORIGIN);
  // Keep the immediate return route without recursively encoding its history.
  const source = new URL(safeFrom, INTERNAL_URL_ORIGIN);
  source.searchParams.delete(BACK_PARAM);
  url.searchParams.set(
    BACK_PARAM,
    `${source.pathname}${source.search}${source.hash}`,
  );
  return `${url.pathname}${url.search}${url.hash}`;
}

export function resolveBackHref(
  fromParam: string | null | undefined,
  fallbackHref: string,
): string {
  return (
    normalizeInternalHref(fromParam) ??
    normalizeInternalHref(fallbackHref) ??
    "/"
  );
}
